import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { DataGrid } from '@mui/x-data-grid';
import { ptPT, enUS } from '@mui/x-data-grid/locales';
import {
    Alert, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, InputAdornment, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography
} from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../auth/AuthProvider';
import useProfessionals from '../../hooks/useProfessionals';
import { reactivateProfessional, disableProfessional, PROFESSIONAL_ROLES, professionalLabels } from '../../services/professionals';
import ProfessionalDialog from './ProfessionalDialog';
import i18n, { t } from '../../i18n';

function AccessTable({ actor, filters, onEdit, onAction }) {
    const query = useProfessionals(actor, filters);
    const locale = i18n.resolvedLanguage === 'en' ? 'en-GB' : 'pt-PT';
    const columns = [
        { field: 'name', headerName: t('Nome'), flex: 1, minWidth: 190 },
        { field: 'email', headerName: t('Email'), flex: 1.2, minWidth: 220 },
        { field: 'role', headerName: t('Perfil'), minWidth: 180, flex: 0.8,
            renderCell: ({ value }) => t(professionalLabels[value]) },
        { field: 'disabled', headerName: t('Estado'), width: 120,
            renderCell: ({ value }) => <Chip size="small" label={t(value ? 'Desativado' : 'Ativo')}
                sx={{ bgcolor: value ? '#ECECEC' : '#EAF3FC', color: value ? 'text.secondary' : 'accent.main' }} /> },
        { field: 'last_access', headerName: t('Último acesso'), minWidth: 175,
            valueFormatter: (value) => {
                if (!value || Number.isNaN(Date.parse(value))) return '—';
                return new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
            } },
        { field: 'actions', headerName: t('Ações'), width: 110, align: 'center', headerAlign: 'center', display: 'flex',
            renderCell: ({ row }) => {
                const self = row.user_uuid === actor.user.uuid && !row.disabled;
                return <Stack direction="row">
                    <Tooltip title={t('Editar profissional')}>
                        <IconButton aria-label={t('Editar profissional')} onClick={() => onEdit(row)}><EditOutlinedIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title={t(self ? 'Não pode desativar o seu próprio acesso.' : row.disabled ? 'Reativar acesso' : 'Desativar acesso')}>
                        <Box component="span"><IconButton disabled={self}
                            aria-label={t(row.disabled ? 'Reativar acesso' : 'Desativar acesso')} onClick={() => onAction(row)}>
                            {row.disabled ? <RestartAltOutlinedIcon /> : <BlockOutlinedIcon />}
                        </IconButton></Box>
                    </Tooltip>
                </Stack>;
            } }
    ].map((column) => ({ sortable: false, ...column }));
    return <>
        {query.isError && <Alert severity="error" sx={{ m: 2 }} action={
            <Button color="inherit" disabled={query.isFetching} onClick={() => query.refetch()}>{t('Tentar novamente')}</Button>
        }>{t('Não foi possível carregar os profissionais.')}</Alert>}
        <DataGrid autoHeight aria-label={t('Profissionais da entidade')} rows={query.isError ? [] : query.data?.rows || []}
            getRowId={(row) => row.role_uuid} columns={columns} loading={query.isFetching}
            rowCount={query.data?.total || 0} paginationMode="server"
            paginationModel={query.paginationModel} onPaginationModelChange={query.changePage}
            pageSizeOptions={[10, 20, 50]} disableRowSelectionOnClick disableColumnFilter disableColumnMenu
            localeText={{
                ...(locale === 'pt-PT' ? ptPT : enUS).components.MuiDataGrid.defaultProps.localeText,
                noRowsLabel: query.isError ? t('Dados indisponíveis') : t('Nenhum profissional encontrado.'),
                MuiTablePagination: {
                    labelRowsPerPage: t('Linhas por página:'),
                    labelDisplayedRows: ({ from, to, count }) => `${from}–${to} ${t('de')} ${count}`
                }
            }}
            sx={{
                minHeight: 220, border: 0, borderRadius: 0,
                '& .MuiTablePagination-root': { overflowY: 'hidden' },
                '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': { bgcolor: '#FAFBFC', color: 'primary.main' },
                '& .MuiDataGrid-columnHeaderTitle': { color: 'primary.main', fontWeight: 700 },
                '& .MuiDataGrid-cell': { borderColor: '#EEF1F3' },
                '& .MuiDataGrid-row:hover': { bgcolor: '#EAF3FC' }
            }}
        />
    </>;
}
AccessTable.propTypes = {
    actor: PropTypes.object.isRequired, filters: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired, onAction: PropTypes.func.isRequired
};

export default function Professionals() {
    const { user, role } = useAuth();
    const actor = { user, role };
    const queryClient = useQueryClient();
    const [selectedRole, setSelectedRole] = useState('all');
    const [disabled, setDisabled] = useState(false);
    const [input, setInput] = useState('');
    const [search, setSearch] = useState('');
    const [dialog, setDialog] = useState(null);
    const [action, setAction] = useState(null);
    const [busy, setBusy] = useState(false);
    const active = useRef(true);
    const lock = useRef(false);
    useEffect(() => {
        active.current = true;
        return () => { active.current = false; };
    }, []);
    const [notice, setNotice] = useState(null);
    const [revision, setRevision] = useState(0);
    useEffect(() => {
        const timeout = setTimeout(() => setSearch(input.trim()), 400);
        return () => clearTimeout(timeout);
    }, [input]);
    if (role?.name !== 'CLINIC_ADMIN') return <Alert severity="warning">{t('Só os administrativos da entidade podem gerir profissionais.')}</Alert>;
    const saved = (recovered = false) => {
        if (!active.current) return;
        void queryClient.invalidateQueries({ queryKey: ['clinic-professionals'] });
        void queryClient.invalidateQueries({ queryKey: ['clinic-overview'] });
        void queryClient.invalidateQueries({ queryKey: ['clinic-professionals-detail'] });
        setRevision((value) => value + 1);
        setDialog(null); setAction(null);
        setNotice({
            severity: recovered ? 'info' : 'success',
            message: recovered ? 'O perfil já existe e a associação foi confirmada. Confirme a receção da notificação de acesso.'
                : 'A alteração do profissional foi concluída.'
        });
    };
    const confirmAction = async () => {
        if (lock.current) return;
        lock.current = true;
        setBusy(true); setNotice(null);
        try {
            if (action.disabled) {
                const result = await reactivateProfessional(actor, action);
                saved(result.recovered);
            } else {
                await disableProfessional(actor, action);
                saved();
            }
        } catch { if (active.current) setNotice({ severity: 'error', message: 'Não foi possível alterar o acesso. Tente novamente.' }); }
        finally { lock.current = false; if (active.current) setBusy(false); }
    };
    const filters = { roles: selectedRole === 'all' ? PROFESSIONAL_ROLES : [selectedRole], disabled, search };
    return <Stack spacing={2}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: 2 }}>
            <ToggleButtonGroup exclusive value={selectedRole}
                onChange={(_, value) => { if (value) setSelectedRole(value); }}
                aria-label={t('Filtrar por perfil')} sx={{
                    p: 0.375, gap: 0.5, bgcolor: '#EEF2F5', borderRadius: 1.5, overflowX: 'auto',
                    '& .MuiToggleButton-root': { border: 0, borderRadius: '5px !important', textTransform: 'none',
                        fontWeight: 700, color: '#53636F', whiteSpace: 'nowrap' },
                    '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
                        bgcolor: 'accent.main', color: '#fff'
                    }
                }}>
                <ToggleButton value="all">{t('Todos')}</ToggleButton>
                <ToggleButton value="DOCTOR">{t('Médicos')}</ToggleButton>
                <ToggleButton value="CLINIC_CARE_GIVER">{t('Técnicos de saúde')}</ToggleButton>
            </ToggleButtonGroup>
            <Button variant="contained" startIcon={<PersonAddAltOutlinedIcon />} onClick={() => { setDialog({}); setNotice(null); }}>
                {t('Adicionar profissional')}
            </Button>
        </Box>
        {notice && <Alert severity={notice.severity}>{t(notice.message)}</Alert>}
        <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderColor: '#E0E5E9' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ p: 2 }}>
                <TextField size="small" value={input} placeholder={t('Pesquisar nome ou email')}
                    inputProps={{ 'aria-label': t('Pesquisar nome ou email') }}
                    onChange={(event) => setInput(event.target.value)} sx={{ width: { xs: '100%', sm: 330 } }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                <TextField select size="small" label={t('Estado')} value={disabled ? 'disabled' : 'active'}
                    onChange={(event) => setDisabled(event.target.value === 'disabled')} sx={{ minWidth: 160 }}>
                    <MenuItem value="active">{t('Ativos')}</MenuItem><MenuItem value="disabled">{t('Desativados')}</MenuItem>
                </TextField>
            </Stack>
            <AccessTable key={JSON.stringify([actor.role.uuid, selectedRole, disabled, search, revision])} actor={actor} filters={filters}
                onEdit={(target) => { setDialog({ target }); setNotice(null); }}
                onAction={(target) => { setAction(target); setNotice(null); }} />
        </Card>
        {dialog && <ProfessionalDialog actor={actor} target={dialog.target} onClose={() => setDialog(null)} onSaved={saved} />}
        <Dialog open={Boolean(action)} onClose={() => { if (!busy) setAction(null); }} fullWidth maxWidth="xs" aria-labelledby="access-action-title">
            <DialogTitle id="access-action-title">{t(action?.disabled ? 'Reativar acesso' : 'Desativar acesso')}</DialogTitle>
            <DialogContent><Typography>{action?.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t(action?.disabled ? 'O perfil será reativado e será enviada uma notificação por email.' : 'Esta ação desativa este perfil de acesso.')}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button disabled={busy} onClick={() => setAction(null)}>{t('Cancelar')}</Button>
                <Button disabled={busy} variant="contained" onClick={confirmAction}>
                    {t(busy ? 'A guardar…' : action?.disabled ? 'Reativar acesso' : 'Desativar acesso')}
                </Button>
            </DialogActions>
        </Dialog>
    </Stack>;
}
